import {
  buildConfigCartLineKey,
  buildConfigSummary,
  getPresetConfigSteps,
  normalizeConfigChoices,
  resolveConfigPriceRub,
  resolveConfigSpecs
} from '../../lib/template-config.js';
import { escapeAttr, formatGroupedInt, formatPriceWithRub } from '../../core/format.js';
import { lazyImageHtml, refreshLazyImages } from '../../core/lazy-image.js';
import { showView } from '../../app/navigation.js';
import { prepareForCartNavigation } from '../../app/photo-modal.js';
import { addConfiguredPresetToCart } from '../cart/cart.js';
import type { PresetItem, TemplateOption, TemplateStep } from '../../types/api.js';

let activePreset: PresetItem | null = null;
let activeStepIdx = 0;
let choices: Record<string, string> = {};
let lastRenderedStepIdx = -1;
let stepNavDirection: 'forward' | 'back' | 'none' = 'none';

function playConfiguratorIntro(): void {
  const content = document.querySelector('#view-template-config .tpl-config-content');
  if (!(content instanceof HTMLElement)) return;
  content.classList.remove('tpl-config-intro');
  void content.offsetWidth;
  content.classList.add('tpl-config-intro');
  window.setTimeout(() => content.classList.remove('tpl-config-intro'), 720);
}

function playStepTransition(
  stepChanged: boolean,
  titleEl: HTMLElement | null,
  descEl: HTMLElement | null,
  optionsEl: HTMLElement
): void {
  const targets = [titleEl, descEl, optionsEl].filter((el): el is HTMLElement => el instanceof HTMLElement);
  for (const el of targets) {
    el.classList.remove('tpl-config-step-anim-forward', 'tpl-config-step-anim-back');
  }
  if (!stepChanged || stepNavDirection === 'none') {
    stepNavDirection = 'none';
    return;
  }
  const animClass =
    stepNavDirection === 'back' ? 'tpl-config-step-anim-back' : 'tpl-config-step-anim-forward';
  void optionsEl.offsetWidth;
  for (const el of targets) {
    el.classList.add(animClass);
  }
  stepNavDirection = 'none';
}

function pulseConfiguratorPrice(priceEl: HTMLElement, nextPrice: string): void {
  if (priceEl.textContent === nextPrice) return;
  priceEl.textContent = nextPrice;
  priceEl.classList.remove('tpl-config-price-pulse');
  void priceEl.offsetWidth;
  priceEl.classList.add('tpl-config-price-pulse');
}

function scrollConfiguratorContentTop(): void {
  const content = document.querySelector('#view-template-config .tpl-config-content');
  if (content instanceof HTMLElement) {
    content.scrollTop = 0;
  }
  try {
    window.scrollTo(0, 0);
  } catch {
    /* ignore */
  }
}

function syncConfiguratorFooterInset(): void {
  const view = document.getElementById('view-template-config');
  const footer = view?.querySelector('.tpl-config-footer');
  if (!(view instanceof HTMLElement) || !(footer instanceof HTMLElement)) return;
  const h = Math.ceil(footer.getBoundingClientRect().height);
  if (h > 0) {
    view.style.setProperty('--tpl-config-footer-h', h + 'px');
  }
}

export function openTemplateConfigurator(item: PresetItem): void {
  prepareForCartNavigation();
  activePreset = item;
  activeStepIdx = 0;
  const steps = getPresetConfigSteps(item);
  choices = normalizeConfigChoices({}, steps);
  lastRenderedStepIdx = -1;
  stepNavDirection = 'none';
  renderConfigurator();
  showView('view-template-config', 'right');
  requestAnimationFrame(() => {
    syncConfiguratorFooterInset();
    scrollConfiguratorContentTop();
    playConfiguratorIntro();
  });
}

function getSteps(): TemplateStep[] {
  return activePreset ? getPresetConfigSteps(activePreset) : [];
}

function currentPriceRub(): number {
  if (!activePreset) return 0;
  return resolveConfigPriceRub(activePreset, choices) || 0;
}

function formatDelta(delta: number): string {
  if (!delta) return '+0 ₽';
  return '+' + formatGroupedInt(delta) + ' ₽';
}

function renderProgress(): void {
  const steps = getSteps();
  const bar = document.getElementById('tpl-config-progress');
  const label = document.getElementById('tpl-config-step-label');
  if (bar) {
    let html = '';
    for (let i = 0; i < steps.length; i++) {
      html +=
        '<span class="tpl-config-progress-seg' +
        (i <= activeStepIdx ? ' is-active' : '') +
        '"></span>';
    }
    bar.innerHTML = html;
  }
  if (label) {
    label.textContent = 'Шаг ' + (activeStepIdx + 1) + ' из ' + steps.length;
  }
}

function renderOptionRadio(step: TemplateStep, opt: TemplateOption): string {
  const selected = choices[String(step.id)] === String(opt.id);
  let html =
    '<button type="button" class="tpl-config-radio' +
    (selected ? ' is-selected' : '') +
    '" data-step-id="' +
    escapeAttr(step.id) +
    '" data-option-id="' +
    escapeAttr(opt.id) +
    '">';
  html += '<span class="tpl-config-radio-mark" aria-hidden="true"></span>';
  html += '<span class="tpl-config-radio-body">';
  html += '<span class="tpl-config-radio-title">' + escapeAttr(opt.label) + '</span>';
  html += '</span>';
  if (opt.priceDelta > 0) {
    html += '<span class="tpl-config-price-badge">' + escapeAttr(formatDelta(opt.priceDelta)) + '</span>';
  }
  html += '</button>';
  return html;
}

function renderOptionCard(step: TemplateStep, opt: TemplateOption): string {
  const selected = choices[String(step.id)] === String(opt.id);
  const img = opt.imageThumb || opt.image || '';
  let html =
    '<button type="button" class="tpl-config-card' +
    (selected ? ' is-selected' : '') +
    '" data-step-id="' +
    escapeAttr(step.id) +
    '" data-option-id="' +
    escapeAttr(opt.id) +
    '">';
  if (img) {
    html +=
      '<span class="tpl-config-card-img-wrap">' +
      lazyImageHtml(img, { loading: 'lazy' }) +
      '</span>';
  } else {
    html += '<span class="tpl-config-card-img-wrap tpl-config-card-img-placeholder"></span>';
  }
  html += '<span class="tpl-config-card-title">' + escapeAttr(opt.label) + '</span>';
  if (opt.badge) {
    html += '<span class="tpl-config-card-badge">' + escapeAttr(opt.badge) + '</span>';
  } else {
    html += '<span class="tpl-config-card-badge">' + escapeAttr(formatDelta(opt.priceDelta)) + '</span>';
  }
  html += '</button>';
  return html;
}

function renderConfigurator(): void {
  const steps = getSteps();
  const step = steps[activeStepIdx];
  const titleEl = document.getElementById('tpl-config-title');
  const descEl = document.getElementById('tpl-config-desc');
  const optionsEl = document.getElementById('tpl-config-options');
  const priceEl = document.getElementById('tpl-config-total');
  const nextBtn = document.getElementById('tpl-config-next-btn') as HTMLButtonElement | null;
  if (!step || !optionsEl || !activePreset) return;

  const stepChanged = lastRenderedStepIdx !== activeStepIdx;
  lastRenderedStepIdx = activeStepIdx;

  if (titleEl) titleEl.textContent = step.title;
  if (descEl) descEl.textContent = step.description || '';
  renderProgress();

  const isCards = step.ui === 'cards';
  optionsEl.className = isCards ? 'tpl-config-options tpl-config-options-cards' : 'tpl-config-options';
  let html = '';
  for (let i = 0; i < step.options.length; i++) {
    const opt = step.options[i];
    html += isCards ? renderOptionCard(step, opt) : renderOptionRadio(step, opt);
  }
  optionsEl.innerHTML = html;
  refreshLazyImages(optionsEl);

  optionsEl.querySelectorAll('[data-option-id]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const stepId = btn.getAttribute('data-step-id') || '';
      const optionId = btn.getAttribute('data-option-id') || '';
      if (!stepId || !optionId) return;
      choices[stepId] = optionId;
      renderConfigurator();
    });
  });

  if (priceEl) pulseConfiguratorPrice(priceEl, formatGroupedInt(currentPriceRub()) + ' ₽');
  if (nextBtn) {
    nextBtn.textContent = activeStepIdx >= steps.length - 1 ? 'В корзину' : 'Далее';
  }

  playStepTransition(stepChanged, titleEl, descEl, optionsEl);

  if (stepChanged) {
    requestAnimationFrame(() => {
      syncConfiguratorFooterInset();
      scrollConfiguratorContentTop();
    });
  }
}

function goBack(): void {
  if (activeStepIdx > 0) {
    stepNavDirection = 'back';
    activeStepIdx -= 1;
    renderConfigurator();
    return;
  }
  if (activePreset) showView('view-preset-detail', 'left');
}

function goNext(): void {
  const steps = getSteps();
  if (activeStepIdx < steps.length - 1) {
    stepNavDirection = 'forward';
    activeStepIdx += 1;
    renderConfigurator();
    return;
  }
  if (!activePreset) return;
  prepareForCartNavigation();
  const normalized = normalizeConfigChoices(choices, steps);
  const priceRub = resolveConfigPriceRub(activePreset, normalized);
  if (priceRub == null) return;
  const lineKey = buildConfigCartLineKey(String(activePreset.id), normalized);
  addConfiguredPresetToCart({
    lineKey,
    templateId: String(activePreset.id),
    item: activePreset,
    priceRub,
    configChoices: normalized,
    configSummary: buildConfigSummary(activePreset, normalized),
    specs: resolveConfigSpecs(activePreset, normalized)
  });
  showView('view-presets');
}

export function initTemplateConfiguratorUi(): void {
  const backHeader = document.getElementById('tpl-config-back-header');
  const backFooter = document.getElementById('tpl-config-back-footer');
  const nextBtn = document.getElementById('tpl-config-next-btn');
  backHeader?.addEventListener('click', goBack);
  backFooter?.addEventListener('click', goBack);
  nextBtn?.addEventListener('click', goNext);

  const view = document.getElementById('view-template-config');
  const footer = view?.querySelector('.tpl-config-footer');
  if (view && footer && typeof ResizeObserver !== 'undefined') {
    try {
      new ResizeObserver(() => syncConfiguratorFooterInset()).observe(footer);
    } catch {
      /* ignore */
    }
  }
  window.addEventListener('resize', syncConfiguratorFooterInset, { passive: true });
}

export function getActiveConfiguratorPreset(): PresetItem | null {
  return activePreset;
}
